import React, { useEffect, useState } from 'react';
import { useApp } from '../contexts/AppContext';
import axios from 'axios';
import { Plug, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function IntegrationStatus() {
  const { t, lang } = useApp();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/integrations/status`)
      .then(r => { setIntegrations(r.data.integrations); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-12 text-slate-400">{t('loading')}</div>;

  const statusConfig = {
    connected: { icon: CheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-50', label: 'Connected', dot: 'status-connected' },
    rate_limited: { icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-50', label: 'Rate Limited', dot: 'status-rate_limited' },
    sandbox: { icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-50', label: 'Sandbox', dot: 'status-sandbox' },
    disconnected: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', label: 'Disconnected', dot: 'status-disconnected' },
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="integration-status">
      <div className="card-flat p-6">
        <div className="flex items-center gap-3 mb-6">
          <Plug size={24} className="text-[#1B2A4A]" />
          <div>
            <h2 className="text-lg font-semibold font-english text-slate-800">{lang === 'bn' ? 'সরকারি সংযোগের অবস্থা' : 'Government Integrations Status'}</h2>
            <p className="text-xs text-slate-400">{lang === 'bn' ? 'সকল সরকারি API সংযোগের অবস্থা' : 'Real-time connectivity status for all GoB API integrations'}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {integrations.map(int => {
            const cfg = statusConfig[int.status] || statusConfig.disconnected;
            const Icon = cfg.icon;
            return (
              <div key={int.name} className={`border border-slate-200 p-4 rounded-sm ${cfg.bg}`} data-testid={`integration-${int.name.replace(/\s/g, '-').toLowerCase()}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`status-dot ${cfg.dot}`} />
                      <span className="text-sm font-semibold text-slate-800 font-english">{int.name}</span>
                    </div>
                    <p className="text-xs font-bangla text-slate-500">{int.name_bangla}</p>
                  </div>
                  <Icon size={20} className={cfg.color} />
                </div>
                <div className="mt-3 flex items-center justify-between text-[10px] text-slate-400">
                  <span>Status: <span className={`font-medium ${cfg.color}`}>{cfg.label}</span></span>
                  <span>Latency: {int.latency_ms}ms</span>
                </div>
                <div className="mt-1 text-[10px] text-slate-300">
                  Last checked: {new Date(int.last_check).toLocaleTimeString()}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="kpi-card text-center">
          <p className="text-xs uppercase tracking-wider text-slate-400 font-english mb-1">{lang === 'bn' ? 'সংযুক্ত' : 'Connected'}</p>
          <p className="kpi-value text-[#1D9E75]">{integrations.filter(i => i.status === 'connected').length}</p>
        </div>
        <div className="kpi-card text-center">
          <p className="text-xs uppercase tracking-wider text-slate-400 font-english mb-1">{lang === 'bn' ? 'ক্ষয়প্রাপ্ত' : 'Degraded'}</p>
          <p className="kpi-value text-[#C9A84C]">{integrations.filter(i => ['rate_limited', 'sandbox'].includes(i.status)).length}</p>
        </div>
        <div className="kpi-card text-center">
          <p className="text-xs uppercase tracking-wider text-slate-400 font-english mb-1">{lang === 'bn' ? 'বিচ্ছিন্ন' : 'Disconnected'}</p>
          <p className="kpi-value text-[#C00000]">{integrations.filter(i => i.status === 'disconnected').length}</p>
        </div>
      </div>
    </div>
  );
}
